import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, OnDestroy, Output } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BaseNotebookCellButtonComponent } from '../base-button.component';
import { DatasetModalService } from '../../dataset-modal/dataset-modal.service';
import { getThoughtspotIntegrated } from '@mode/shared/util-js';
import { CellExportFacade, ContentType } from '@mode/notebooks/contract';
import { Observable, Subject, takeUntil, tap } from 'rxjs';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { MetaTags, MetaTagsService } from '@mode/shared/util-js';

@Component({
  selector: 'mode-notebook-use-output-menu',
  templateUrl: './notebook-use-output-menu.component.html',
  styleUrls: ['./notebook-use-output-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotebookUseOutputMenuComponent extends BaseNotebookCellButtonComponent implements OnDestroy {
  private readonly _destroyed$ = new Subject<void>();

  @Input() isOutputUsed = false;
  @Input() disabled = false;

  isCellExported = false;
  cellContentType!: ContentType;

  isCellExported$: Observable<boolean>;
  isExportingInProgress$: Observable<boolean>;
  isPython$: Observable<boolean>;

  @Output()
  toggle: EventEmitter<{ cellToken: string; inLayout: boolean }> = new EventEmitter();

  buttonText = '';
  thoughtspotIntegrated = getThoughtspotIntegrated(this.document);

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private datasetModalService: DatasetModalService,
    private cellExportFacade: CellExportFacade,
    private analyticsService: AnalyticsService,
    private metaTag: MetaTagsService
  ) {
    super();

    this.isCellExported$ = this.cellExportFacade.selectedCellIsExported$.pipe(
      tap((isCellExported) => (this.isCellExported = isCellExported))
    );
    this.isExportingInProgress$ = this.cellExportFacade.isExporting$;
    this.cellExportFacade.selectedCellContentType$
      .pipe(takeUntil(this._destroyed$))
      .subscribe((selectedCellContentType) => (this.cellContentType = selectedCellContentType));
    this.isPython$ = this.cellExportFacade.isPython$;
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  toggleReportState() {
    const eventName = this.isOutputUsed
      ? AnalyticsEvent.Name.ExportDatasetAddToReportBuilder
      : AnalyticsEvent.Name.ExportDatasetRemoveToReportBuilder;
    const action = this.isOutputUsed
      ? `${AnalyticsEvent.Name.ExportDatasetOutput} - ${AnalyticsEvent.Name.ExportDatasetAddToReportBuilder}`
      : `${AnalyticsEvent.Name.ExportDatasetOutput} - ${AnalyticsEvent.Name.ExportDatasetRemoveToReportBuilder}`;

    this.trackAnalyticsEvent(eventName, AnalyticsEvent.SubContext.ExportDatasetOutputClicked, action);

    this.toggle.emit({ cellToken: this.cellToken, inLayout: this.isOutputUsed });
  }

  handleCreateDataset() {
    this.trackAnalyticsEvent(
      AnalyticsEvent.Name.ExportDatasetCreateDataset,
      AnalyticsEvent.SubContext.ExportDatasetCreateDatasetClicked,
      `${AnalyticsEvent.Name.ExportDatasetOutput} - ${AnalyticsEvent.Name.ExportDatasetCreateDataset}`
    );
    this.datasetModalService.openCreateDatasetModal(this.cellToken, this.cellContentType);
  }

  onDropdownClick() {
    this.trackAnalyticsEvent(
      AnalyticsEvent.Name.ExportDatasetOutput,
      AnalyticsEvent.SubContext.ExportDatasetOutputClicked,
      AnalyticsEvent.SubContext.ExportDatasetOutputClicked
    );
  }

  private trackAnalyticsEvent(eventName: AnalyticsEvent.Name, subContext: string, action: string): void {
    this.analyticsService.track(eventName, {
      context: this.analyticsService.context,
      location: `${subContext} CTA`,
      action: action,
      name: this.metaTag.getTag(MetaTags.CurrentUsername),
    });
  }
}
